import { Button, Checkbox, Icon } from '@acpaas-ui/react-components';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { set } from '../../../../actions/breadcrumbs.actions';
import { fetchReset } from '../../../../actions/fetch.actions';
import FileUpload from '../../../../components/core/FileUpload';
import RichTextArea from '../../../../components/forms/RichTextArea';
import SelectField from '../../../../components/forms/SelectField';
import TextField from '../../../../components/forms/TextField';
import OfferingAdminDetailFetching from '../../../../components/offerings/OfferingAdminDetailFetching';
import { ADMIN_DETAIL_OFFERING_BREADCRUMBS } from '../../../../constants/breadcrumbs.constants';
import { MODALS } from '../../../../constants/modals.constants';
import { OFFERING_PERMISSIONS_FIELDS } from '../../../../constants/offering.constants';
import { ADMIN_PATH, ADMIN_TAB_PATHS } from '../../../../constants/paths.constants';
import { DOSSIER_TYPES, DOSSIER_TYPE_OPTIONS } from '../../../../constants/settings.constants';
import formValidators from '../../../../form-validators';
import { useAdminRouteGuard } from '../../../../hooks/admin/useAdminRouteGuard.hook';
import { useModal } from '../../../../hooks/useModal.hook';
import { useResourceInput } from '../../../../hooks/useResourceInput';
import { useSingleResource } from '../../../../hooks/useSingleResource.hook';
import { useTableResource } from '../../../../hooks/useTableResource.hook';
import DeleteWarningModal from '../../../../modals/DeleteWarningModal';
import ImagePreviewModal from '../../../../modals/ImagePreviewModal';
import { ADMIN_OFFERINGS_RESOURCE } from '../../../../resources/admin/admin.offerings.resource';
import { OFFERING_FORM_OPTIONS_RESOURCE } from '../../../../resources/offering.form.options.resource';
import { OFFERING_THEMES_RESOURCE } from '../../../../resources/offering.themes.resource';
import adminOfferingsService from '../../../../services/api/admin.offerings.service';

const resource = ADMIN_OFFERINGS_RESOURCE;

const AdminOfferingDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  useAdminRouteGuard();

  const [isUpdatingImages, setIsUpdatingImages] = useState(false);
  const [openedImageUrl, setOpenedImageUrl] = useState('');
  const deleteModal = useModal(MODALS.ADMIN_OFFERING_CONFIRM_DELETE);
  const imagePreviewModal = useModal(MODALS.IMAGE_PREVIEW_MODAL);
  const { isLoading: isLoadingThemes, data: themesData } = useTableResource(OFFERING_THEMES_RESOURCE, {
    isFetched: true
  });
  const { data: formOptions, isLoading: isLoadingForms } = useTableResource(OFFERING_FORM_OPTIONS_RESOURCE, {
    isFetched: true
  });
  const themesOptions = themesData?.map((t) => ({ value: t.name, label: t.name }));
  const connectedResource = useSingleResource(params.id, { ...resource, updateFn: adminOfferingsService.update });
  const resourceWithValidation = {
    ...connectedResource,
    validators: {
      dossierType: formValidators.required,
      shortDescription: (value) => formValidators.maxLength(value, 255),
      description: (value) => formValidators.maxLength(value, 10000)
    }
  };
  const titleInput = useResourceInput('title', resourceWithValidation);
  const shortDescriptionInput = useResourceInput('shortDescription', resourceWithValidation);
  const descriptionInput = useResourceInput('description', resourceWithValidation);
  const formSlugInput = useResourceInput('formSlug', resourceWithValidation);
  const dossierTypeInput = useResourceInput('dossierType', resourceWithValidation);
  const themesInput = useResourceInput('themes', resourceWithValidation);

  useEffect(() => {
    if (resourceWithValidation.isFetching) {
      dispatch(set(ADMIN_DETAIL_OFFERING_BREADCRUMBS.LOADING()));
    }
    if (resourceWithValidation.isFetched) {
      dispatch(set(ADMIN_DETAIL_OFFERING_BREADCRUMBS.LOADED({ name: resourceWithValidation.data?.title })));
    }
  }, [
    dispatch,
    resourceWithValidation.isFetching,
    resourceWithValidation.isFetched,
    resourceWithValidation.data?.title
  ]);

  useEffect(() => {
    return () => {
      dispatch(fetchReset(OFFERING_THEMES_RESOURCE));
      dispatch(fetchReset(OFFERING_FORM_OPTIONS_RESOURCE));
    };
  }, [dispatch]);

  const handleDelete = (onError, onSuccess) => {
    adminOfferingsService.remove(params.id, {
      onError,
      onSuccess: () => {
        onSuccess();
        dispatch(fetchReset(ADMIN_OFFERINGS_RESOURCE));
        navigate(`${ADMIN_PATH}${ADMIN_TAB_PATHS.OFFERING.path}`);
      }
    });
  };

  const _onImageUpdateSuccess = () => {
    setIsUpdatingImages(false);
    resourceWithValidation.refetch();
  };

  const handleImageUpload = (files) => {
    setIsUpdatingImages(true);
    adminOfferingsService.addImage(params.id, files[0], {
      onSuccess: _onImageUpdateSuccess,
      onError: () => {
        setIsUpdatingImages(false);
      }
    });
  };

  const handleImageDelete = (image) => {
    setIsUpdatingImages(true);
    adminOfferingsService.removeImage(params.id, image.name, {
      onSuccess: _onImageUpdateSuccess,
      onError: () => {
        setIsUpdatingImages(false);
      }
    });
  };

  const handleImagePreview = (image) => {
    const imgIndex = resourceWithValidation.data?.images?.findIndex((i) => i === image.name);
    setOpenedImageUrl(resourceWithValidation.data?.imageUrls?.[imgIndex]);
    imagePreviewModal.onOpen();
  };

  const inputCheckbox = (e) => {
    const updateBody = { [e.target.name]: e.target.checked };
    if (e.target.name === 'visibleUnauthorized' && !!e.target.checked) {
      updateBody.visibleSchools = false;
      updateBody.visiblePartners = false;
      updateBody.visibleCity = false;
    }
    if (e.target.name !== 'visibleUnauthorized' && !!e.target.checked) {
      updateBody.visibleUnauthorized = false;
    }
    resourceWithValidation.setMultipleValues(updateBody, {
      onSuccess: () => {},
      onError: () => {}
    });
  };

  return (
    <div>
      {resourceWithValidation.isFetching ? (
        <OfferingAdminDetailFetching />
      ) : resourceWithValidation.error ? (
        <div className="u-text-danger u-flex">
          <span className="u-margin-right">Er is iets mis gegaan, probeer het alstublieft nogmaals</span>
          <Icon name="ai-alert-triangle" />
        </div>
      ) : (
        <div>
          <h5 className="u-margin-bottom">{resourceWithValidation.data?.title}</h5>
          <div className="row u-margin-bottom-md">
            <div className="col-xs-12">
              <TextField label="Titel" required {...titleInput} />
            </div>
          </div>
          <div className="row u-margin-bottom-md">
            <div className="col-xs-12">
              <TextField label="Korte beschrijving zichtbaar op het overzichtscherm" {...shortDescriptionInput} />
            </div>
          </div>
          <div className="row u-margin-bottom-md">
            <div className="col-xs-12">
              {/* <TextArea label="Volledige beschrijving" {...descriptionInput} /> */}
              <RichTextArea
                label="Volledige beschrijving"
                {...descriptionInput}
                isSubmitting={resourceWithValidation.isSubmitting}
              />
            </div>
          </div>
          <div className="row u-margin-bottom-md">
            <div className="col-xs-12">
              <div className="a-input">
                <label className="a-input__label">Afbeelding</label>
              </div>
              <FileUpload
                acceptedFormat="image/*"
                disabled={isUpdatingImages}
                isLoading={isUpdatingImages}
                files={resourceWithValidation.data?.images?.map((i) => ({ name: i, size: 0 })) ?? []}
                onOpen={handleImagePreview}
                onDelete={handleImageDelete}
                onChange={handleImageUpload}
              />
            </div>
          </div>
          <div className="row u-margin-bottom-md">
            <div className="col-xs-12">
              <SelectField
                label="Dossiertype dat aangemaakt moet worden bij aanvraag"
                {...dossierTypeInput}
                options={DOSSIER_TYPE_OPTIONS}
                isSubmitting={resourceWithValidation.isSubmitting}
                disableSearch
                required
              />
            </div>
          </div>
          {dossierTypeInput.value && dossierTypeInput.value !== DOSSIER_TYPES.NONE && (
            <div className="row u-margin-bottom-md">
              <div className="col-xs-12">
                <SelectField
                  label="Aanvraagformulier uit Formscomposer"
                  {...formSlugInput}
                  isSubmitting={resourceWithValidation.isSubmitting || isLoadingForms}
                  options={formOptions}
                />
              </div>
            </div>
          )}
          <div className="row u-margin-bottom-md">
            <div className="col-xs-12">
              <SelectField
                label="Label"
                {...themesInput}
                isSubmitting={resourceWithValidation.isSubmitting || isLoadingThemes}
                options={themesOptions}
                flyoutDirection="up"
                multiple
              />
            </div>
          </div>
          <div className="row u-margin-bottom-lg">
            <div className="col-xs-12">
              <label className="a-input__label">Aanbod is zichtbaar voor:</label>
              <div className="u-margin-top-xs">
                {OFFERING_PERMISSIONS_FIELDS.map((item) => (
                  <Checkbox
                    id={`checkbox-${item?.field}-id`}
                    key={`checkbox-${item?.field}-id`}
                    name={item?.field}
                    label={item?.label}
                    checked={!!resourceWithValidation.getValue(item?.field)}
                    onChange={inputCheckbox}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="row">
            {resourceWithValidation.error && (
              <div className="col-xs-12 u-margin-bottom">
                <p className="u-text-danger">{resourceWithValidation.error}</p>
              </div>
            )}
            <div className="col-xs-12">
              <Button iconRight="ai-bin" className="u-margin-right" type="danger" outline onClick={deleteModal.onOpen}>
                Verwijderen
              </Button>
            </div>
          </div>
          <DeleteWarningModal {...deleteModal} onConfirm={handleDelete} />
          {imagePreviewModal.isOpen && <ImagePreviewModal {...imagePreviewModal} src={openedImageUrl} />}
        </div>
      )}
    </div>
  );
};

export default AdminOfferingDetails;
